import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc, 
  serverTimestamp, 
  getDoc,
  Timestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { NetworkRequest, Relationship, RelationshipType } from '../types/Relationship';
import { getAuthHeaders } from '../utils/auth';

// Collection constants
const NETWORK_REQUESTS_COLLECTION = 'networkRequests';
const RELATIONSHIPS_COLLECTION = 'relationships';

/**
 * Send a network connection request to another user
 */
export const sendNetworkRequest = async (request: Omit<NetworkRequest, 'id' | 'status' | 'createdAt'>): Promise<string> => {
  try {
    // Check if a request already exists
    const existingQuery = query(
      collection(db, NETWORK_REQUESTS_COLLECTION),
      where('fromUserId', '==', request.fromUserId),
      where('toUserEmail', '==', request.toUserEmail),
      where('status', '==', 'pending')
    );
    
    const existingResults = await getDocs(existingQuery);
    
    if (!existingResults.empty) {
      // Return the existing request ID
      return existingResults.docs[0].id;
    }
    
    // Check if this person is already in the user's network
    const relationshipsQuery = query(
      collection(db, RELATIONSHIPS_COLLECTION),
      where('userId', '==', request.fromUserId),
      where('relationToUserId', '==', request.toUserId)
    );
    
    const relationshipResults = await getDocs(relationshipsQuery);
    
    if (!relationshipResults.empty) {
      throw new Error('This person is already in your network');
    }
    
    // Create a new request with null for undefined values
    const sanitizedRequest = {
      fromUserId: request.fromUserId,
      fromUserName: request.fromUserName,
      fromUserEmail: request.fromUserEmail,
      fromUserPhoto: request.fromUserPhoto || null,
      toUserId: request.toUserId || null,
      toUserName: request.toUserName || null,
      toUserEmail: request.toUserEmail,
      toUserPhoto: request.toUserPhoto || null,
      eventId: request.eventId || null,
      eventTitle: request.eventTitle || null,
      relationshipType: request.relationshipType || null,
      relationshipLabel: request.relationshipLabel || null,
      message: request.message || null,
      status: 'pending',
      createdAt: new Date()
    };
    
    const docRef = await addDoc(
      collection(db, NETWORK_REQUESTS_COLLECTION),
      {
        ...sanitizedRequest,
        createdAt: serverTimestamp()
      }
    );
    
    return docRef.id;
    
  } catch (error) {
    console.error('Error sending network request:', error);
    throw error;
  }
};

/**
 * Get all pending network requests for a user (both sent and received)
 */
export const getNetworkRequests = async (userId: string): Promise<NetworkRequest[]> => {
  try {
    // Get requests sent by the user
    const sentQuery = query(
      collection(db, NETWORK_REQUESTS_COLLECTION),
      where('fromUserId', '==', userId)
    );
    
    const sentResults = await getDocs(sentQuery);
    
    // Get requests received by the user
    const receivedQuery = query(
      collection(db, NETWORK_REQUESTS_COLLECTION),
      where('toUserId', '==', userId)
    );
    
    const receivedResults = await getDocs(receivedQuery);
    
    // Alternative: If user hasn't registered, check by email
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userEmail = userDoc.exists() ? userDoc.data().email : null;
    
    let emailResults = { docs: [] as any[] };
    if (userEmail) {
      const emailQuery = query(
        collection(db, NETWORK_REQUESTS_COLLECTION),
        where('toUserEmail', '==', userEmail)
      );
      
      emailResults = await getDocs(emailQuery);
    }
    
    // Combine results
    const requests: NetworkRequest[] = [];
    
    // Process sent requests
    sentResults.forEach(docSnap => {
      const data = docSnap.data();
      requests.push({
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined
      } as NetworkRequest);
    });
    
    // Process received requests
    receivedResults.forEach(docSnap => {
      const data = docSnap.data();
      requests.push({
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined
      } as NetworkRequest);
    });
    
    // Process email-based requests
    emailResults.docs.forEach(docSnap => {
      // Check if we already have this request from the userId query
      if (!requests.some(r => r.id === docSnap.id)) {
        const data = docSnap.data();
        requests.push({
          id: docSnap.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined
        } as NetworkRequest);
      }
    });
    
    return requests;
    
  } catch (error) {
    console.error('Error getting network requests:', error);
    throw error;
  }
};

/**
 * Get pending network requests for users who attended a specific event
 */
export const getEventAttendeeNetworkRequests = async (eventId: string, userId: string): Promise<NetworkRequest[]> => {
  try {
    const requestsQuery = query(
      collection(db, NETWORK_REQUESTS_COLLECTION),
      where('eventId', '==', eventId),
      where('toUserId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const results = await getDocs(requestsQuery);
    
    const requests: NetworkRequest[] = [];
    
    results.forEach(docSnap => {
      const data = docSnap.data();
      requests.push({
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : data.updatedAt ? new Date(data.updatedAt) : undefined
      } as NetworkRequest);
    });
    
    return requests;
    
  } catch (error) {
    console.error('Error getting event network requests:', error);
    throw error;
  }
};

/**
 * Accept a network request and create a bidirectional relationship
 * Calls backend API to send acceptance email
 */
export const acceptNetworkRequest = async (requestId: string, userId: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/network/accept/${requestId}`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to accept network request');
    }

    const result = await response.json();
    console.log('✅ Network request accepted:', result);
  } catch (error) {
    console.error('Error accepting network request:', error);
    throw error;
  }
};

/**
 * Decline a network request
 * Calls backend API for proper authorization
 */
export const declineNetworkRequest = async (requestId: string, userId: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/network/decline/${requestId}`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to decline network request');
    }

    const result = await response.json();
    console.log('✅ Network request declined:', result);
  } catch (error) {
    console.error('Error declining network request:', error);
    throw error;
  }
};

/**
 * Cancel a network request (as the sender)
 */
export const cancelNetworkRequest = async (requestId: string, userId: string): Promise<void> => {
  try {
    // Get the request
    const requestDoc = await getDoc(doc(db, NETWORK_REQUESTS_COLLECTION, requestId));
    
    if (!requestDoc.exists()) {
      throw new Error('Network request not found');
    }
    
    const requestData = requestDoc.data() as NetworkRequest;
    
    // Verify this user is the sender
    if (requestData.fromUserId !== userId) {
      throw new Error('You are not authorized to cancel this request');
    }
    
    // Delete the request
    await deleteDoc(doc(db, NETWORK_REQUESTS_COLLECTION, requestId));
    
  } catch (error) {
    console.error('Error canceling network request:', error);
    throw error;
  }
};

export const updateNetworkConnection = async (
  connectionId: string, 
  status: 'accepted' | 'declined'
): Promise<void> => {
  try {
    const connectionRef = doc(db, 'networkConnections', connectionId);
    await updateDoc(connectionRef, {
      status,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating network connection:', error);
    throw error;
  }
};

/**
 * Get network statistics for a user including total connections and connections by source
 */
export const getNetworkStats = async (userId: string): Promise<{
  totalConnections: number;
  connectionsByTrip: number;
  connectionsByEvent: number;
  connectionsByDirect: number;
  connectionsByNetworkInvite: number;
  totalTrips: number;
  totalEvents: number;
  lastUpdated: string;
}> => {
  try {
    const headers = await getAuthHeaders();
    
    const response = await fetch(`/api/network/stats/${userId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch network statistics');
    }

    return data.stats;
  } catch (error) {
    console.error('Error fetching network stats:', error);
    throw error;
  }
}; 